import { createBrowserRouter } from 'react-router-dom';

import { AppLayout } from '@/components/layout/AppLayout';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { ABScreen } from '@/screens/app/ABScreen';
import { ApiKeysScreen } from '@/screens/app/ApiKeysScreen';
import { AutomationAdvanced } from '@/screens/app/AutomationAdvanced';
import { AutomationsScreen } from '@/screens/app/AutomationsScreen';
import { CodeScreen } from '@/screens/app/CodeScreen';
import { DashboardScreen } from '@/screens/app/DashboardScreen';
import { EditorScreen } from '@/screens/app/EditorScreen';
import { GalleryScreen } from '@/screens/app/GalleryScreen';
import { IntegrationsMarketplace } from '@/screens/app/IntegrationsMarketplace';
import { PreviewScreen } from '@/screens/app/PreviewScreen';
import { SendersScreen } from '@/screens/app/SendersScreen';
import { SendsScreen } from '@/screens/app/SendsScreen';
import { SettingsScreen } from '@/screens/app/SettingsScreen';
import { SmtpOnboardWizard } from '@/screens/app/smtp-onboard/SmtpOnboardWizard';
import { SMTPScreen } from '@/screens/app/SMTPScreen';
import { TemplatesScreen } from '@/screens/app/TemplatesScreen';
import { TokensScreen } from '@/screens/app/TokensScreen';
import { TrackingDomainsScreen } from '@/screens/app/TrackingDomainsScreen';
import { VariablesScreen } from '@/screens/app/VariablesScreen';
import { WebhooksScreen } from '@/screens/app/WebhooksScreen';
import { AuthCallback } from '@/screens/auth/AuthCallback';
import { Login } from '@/screens/auth/Login';
import { Signup } from '@/screens/auth/Signup';
import { VerifyMagicLink } from '@/screens/auth/VerifyMagicLink';
import { CheckoutScreen } from '@/screens/checkout/CheckoutScreen';
import { DesignSystemScreen } from '@/screens/design/DesignSystemScreen';
import { AcceptInvitation } from '@/screens/invitations/AcceptInvitation';
import { ChangelogPage } from '@/screens/marketing/ChangelogPage';
import { ComingSoonPage } from '@/screens/marketing/ComingSoonPage';
import { ContactPage } from '@/screens/marketing/ContactPage';
import { DpaPage } from '@/screens/marketing/DpaPage';
import { ExportPackLanding } from '@/screens/marketing/ExportPackLanding';
import { MainLanding } from '@/screens/marketing/MainLanding';
import { PricingPage } from '@/screens/marketing/PricingPage';
import { PrivacyPage } from '@/screens/marketing/PrivacyPage';
import { StatusPage } from '@/screens/marketing/StatusPage';
import { TermsPage } from '@/screens/marketing/TermsPage';
import { NotFound } from '@/screens/NotFound';
import { OnboardingWizard } from '@/screens/onboarding/OnboardingWizard';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const router: any = createBrowserRouter([
  {
    path: '/',
    element: <MarketingLayout />,
    children: [
      { index: true, element: <MainLanding /> },
      { path: 'pricing', element: <PricingPage /> },
      { path: 'export-pack', element: <ExportPackLanding /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: 'dpa', element: <DpaPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'status', element: <StatusPage /> },
      {
        path: 'docs',
        element: (
          <ComingSoonPage
            eyebrow="Documentation"
            title="Docs are landing soon"
            blurb="Until then, the OpenAPI explorer at /api/docs is the single source of truth for every endpoint, and the README in the repo covers local setup. Email sales@emailforge.app for whitepapers."
            primaryCta={{ label: 'Open API explorer', to: '/api/docs' }}
          />
        ),
      },
      { path: 'changelog', element: <ChangelogPage /> },
      {
        path: 'about',
        element: (
          <ComingSoonPage
            eyebrow="About"
            title="A team page is on the way"
            blurb="Emailforge is the first product in the Contra Products suite. We're a small team of senior engineers and designers — happy to chat if you'd like to know more before the about page is ready."
            primaryCta={{ label: 'Say hi', to: '/contact' }}
          />
        ),
      },
      {
        path: 'blog',
        element: (
          <ComingSoonPage
            eyebrow="Blog"
            title="Engineering posts coming soon"
            blurb="We're drafting posts on multi-tenant Postgres, deliverability tuning, and the compiler that turns React Email into a constrained block AST. Subscribe to /status to be notified."
            primaryCta={{ label: 'Status & updates', to: '/status' }}
          />
        ),
      },
      {
        path: 'careers',
        element: (
          <ComingSoonPage
            eyebrow="Careers"
            title="Not actively hiring"
            blurb="We're a tiny team for now. If you're an exceptional senior product engineer with email or deliverability experience, we'd still love to talk."
            primaryCta={{ label: 'Reach out', to: '/contact' }}
          />
        ),
      },
    ],
  },
  { path: '/signup', element: <Signup /> },
  { path: '/login', element: <Login /> },
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/auth/verify', element: <VerifyMagicLink /> },
  { path: '/invitations/accept', element: <AcceptInvitation /> },
  { path: '/onboarding/*', element: <OnboardingWizard /> },
  { path: '/checkout/:plan', element: <CheckoutScreen /> },
  { path: '/_design', element: <DesignSystemScreen /> },

  {
    path: '/app',
    element: <AppLayout />,
    children: [
      { index: true, element: <DashboardScreen /> },
      { path: 'templates', element: <TemplatesScreen /> },
      { path: 'templates/:templateId', element: <EditorScreen /> },
      { path: 'templates/:templateId/code', element: <CodeScreen /> },
      { path: 'templates/:templateId/variables', element: <VariablesScreen /> },
      { path: 'templates/:templateId/preview', element: <PreviewScreen /> },
      { path: 'gallery', element: <GalleryScreen /> },
      { path: 'sends', element: <SendsScreen /> },
      { path: 'ab', element: <ABScreen /> },
      { path: 'tokens', element: <TokensScreen /> },
      { path: 'smtp', element: <SMTPScreen /> },
      { path: 'smtp/onboard', element: <SmtpOnboardWizard /> },
      { path: 'senders', element: <SendersScreen /> },
      { path: 'automations', element: <AutomationsScreen /> },
      { path: 'automations/:automationId/advanced', element: <AutomationAdvanced /> },
      { path: 'integrations', element: <IntegrationsMarketplace /> },
      { path: 'api-keys', element: <ApiKeysScreen /> },
      { path: 'webhooks', element: <WebhooksScreen /> },
      { path: 'tracking-domains', element: <TrackingDomainsScreen /> },
      { path: 'settings', element: <SettingsScreen /> },
    ],
  },

  { path: '*', element: <NotFound /> },
]);
